import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DollarSign, Target, BarChart3, Receipt,
  ArrowRight, X, PiggyBank
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface GettingStartedProps {
  onSkip?: () => void;
  onComplete?: () => void;
}

const steps = [
  { icon: DollarSign, title: 'Add a Transaction', description: 'Record your first income or expense', link: '/dashboard/transactions', color: 'bg-blue-500' },
  { icon: Target, title: 'Set a Budget', description: 'Plan your spending with categories', link: '/dashboard/budgets', color: 'bg-purple-500' },
  { icon: PiggyBank, title: 'Create a Savings Goal', description: 'Track progress toward your goals', link: '/dashboard/savings', color: 'bg-emerald-500' },
  { icon: Receipt, title: 'Track Bills', description: 'Never miss a payment', link: '/dashboard/bills', color: 'bg-orange-500' },
];

export function GettingStarted({ onSkip, onComplete }: GettingStartedProps = {}) {
  const handleDismiss = () => {
    onSkip?.();
    onComplete?.();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Welcome to KwachaLite! 👋</h2>
          <p className="text-slate-500 mt-1">Get started by completing one of these steps.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-slate-400 hover:text-slate-600 -mt-1">
          <X className="w-4 h-4 mr-1" /> Skip
        </Button>
      </div>

      {/* Steps grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {steps.map((step) => (
          <Link key={step.link} to={step.link} onClick={handleDismiss}>
            <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-md transition-shadow cursor-pointer group">
              <CardContent className="p-5 flex items-center gap-4">
                <div className={`w-11 h-11 ${step.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <step.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm">{step.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Dismiss */}
      <div className="text-center">
        <Button variant="ghost" onClick={handleDismiss} className="text-slate-400 hover:text-slate-600 text-sm">
          I'll explore on my own →
        </Button>
      </div>
    </div>
  );
}
